/**
 * ⬇️ SPACE EXPORT - Module Export de données
 * Export de données style vaisseau spatial
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, FileJson, FileText, Users, ShoppingCart,
  Loader2, CheckCircle, Database, HardDrive
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Carte d'export
const ExportCard = ({ icon: Icon, title, description, color, onExport, isLoading }) => {
  const colorClasses = {
    cyan: 'border-cyan-500/50 hover:border-cyan-500 bg-cyan-500/5 hover:bg-cyan-500/10',
    pink: 'border-pink-500/50 hover:border-pink-500 bg-pink-500/5 hover:bg-pink-500/10',
    green: 'border-green-500/50 hover:border-green-500 bg-green-500/5 hover:bg-green-500/10',
    purple: 'border-purple-500/50 hover:border-purple-500 bg-purple-500/5 hover:bg-purple-500/10',
  };
  
  const iconColors = {
    cyan: 'text-cyan-400',
    pink: 'text-pink-400',
    green: 'text-green-400',
    purple: 'text-purple-400',
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`backdrop-blur-xl rounded-2xl p-6 border-2 transition-all cursor-pointer ${colorClasses[color]}`}
      onClick={onExport}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl bg-[#1a1a25] border border-gray-700 ${iconColors[color]}`}>
          <Icon className="w-8 h-8" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-white mb-1">{title}</h3>
          <p className="text-sm text-gray-400 mb-4">{description}</p>
          <Button
            disabled={isLoading}
            className={`w-full bg-gradient-to-r ${
              color === 'cyan' ? 'from-cyan-500 to-blue-600' :
              color === 'pink' ? 'from-pink-500 to-purple-600' :
              color === 'green' ? 'from-green-500 to-emerald-600' :
              'from-purple-500 to-indigo-600'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Module Export principal
const SpaceExport = () => {
  const [loadingExport, setLoadingExport] = useState(null);
  const [recentExports, setRecentExports] = useState([]);

  // Fonction d'export générique
  const handleExport = async (type, exportFn, filename) => {
    setLoadingExport(type);
    try {
      const data = await exportFn();
      
      // Créer et télécharger le fichier
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Ajouter à l'historique
      setRecentExports(prev => [
        { type, filename: link.download, date: new Date().toISOString() },
        ...prev.slice(0, 4)
      ]);
      
      toast.success(`📦 Export "${type}" téléchargé !`);
    } catch (error) {
      toast.error(`Erreur lors de l'export: ${error.message}`);
    } finally {
      setLoadingExport(null);
    }
  };

  // Export Ventes
  const handleExportSales = () => {
    handleExport('ventes', async () => {
      const data = await api.getAdminStats?.() || {};
      return {
        exportDate: new Date().toISOString(),
        type: 'sales',
        data: data.salesData || data
      };
    }, 'export_ventes');
  };

  // Export Clients
  const handleExportCustomers = () => {
    handleExport('clients', async () => {
      const customers = await api.getAllCustomers?.() || [];
      return {
        exportDate: new Date().toISOString(),
        type: 'customers',
        count: customers.length,
        data: customers
      };
    }, 'export_clients');
  };

  // Export Cartes fidélité
  const handleExportCards = () => {
    handleExport('cartes', async () => {
      const stats = await api.getAdminStats?.() || {};
      return {
        exportDate: new Date().toISOString(),
        type: 'loyalty_cards',
        data: stats.cardsData || { totalCards: stats.activeCards || 0 }
      };
    }, 'export_cartes_fidelite');
  };

  // Export Complet
  const handleExportAll = () => {
    handleExport('complet', async () => {
      const [stats, customers] = await Promise.all([
        api.getAdminStats?.() || {},
        api.getAllCustomers?.() || []
      ]);
      return {
        exportDate: new Date().toISOString(),
        type: 'full_backup',
        stats,
        customers,
        meta: {
          totalCustomers: customers.length,
          generatedBy: 'Space Admin Export'
        }
      };
    }, 'export_complet');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-500">
            ⬇️ EXPORT
          </h1>
          <p className="text-gray-500 text-sm">Téléchargement des données</p>
        </div>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Database className="w-4 h-4" />
          <span>Format JSON</span>
        </div>
      </div>

      {/* Cartes d'export */}
      <div className="grid md:grid-cols-2 gap-6">
        <ExportCard
          icon={ShoppingCart}
          title="Export Ventes"
          description="Téléchargez les données de ventes quotidiennes et statistiques de revenus"
          color="cyan"
          onExport={handleExportSales}
          isLoading={loadingExport === 'ventes'}
        />
        
        <ExportCard
          icon={Users}
          title="Export Clients"
          description="Liste complète des clients avec leurs statistiques et historique"
          color="pink"
          onExport={handleExportCustomers}
          isLoading={loadingExport === 'clients'}
        />
        
        <ExportCard
          icon={FileText}
          title="Export Cartes Fidélité"
          description="Données des cartes de fidélité, tampons et récompenses"
          color="green"
          onExport={handleExportCards}
          isLoading={loadingExport === 'cartes'}
        />
        
        <ExportCard
          icon={HardDrive}
          title="Export Complet"
          description="Sauvegarde complète de toutes les données du système"
          color="purple"
          onExport={handleExportAll}
          isLoading={loadingExport === 'complet'}
        />
      </div>

      {/* Historique des exports */}
      {recentExports.length > 0 && (
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-gray-700 bg-[#12121a]/60">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileJson className="w-5 h-5 text-gray-400" />
            📋 Exports récents
          </h3>
          <div className="space-y-2">
            {recentExports.map((exp, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between p-3 rounded-xl bg-[#1a1a25] border border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-white text-sm font-medium">{exp.filename}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(exp.date).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
                <span className="px-2 py-1 rounded-lg bg-green-500/20 text-green-400 text-xs">
                  Téléchargé
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-pink-500/10 border border-white/10">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <p className="text-white font-semibold mb-1">Format d'export</p>
            <p className="text-gray-400 text-sm">
              Tous les exports sont au format JSON, compatible avec Excel, Google Sheets et la plupart des outils d'analyse.
              Les données sensibles sont automatiquement protégées.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceExport;
